import Button from "@/elements/Button";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import {
  DispositivoType,
  MappaturaAllarmiType,
  MascheraDispositivo,
  ModelloDispositivoDto,
  getGetModelliDispositivoQueryKey,
  useCreateModelloDispositivo,
  useGetModelliDispositivo,
  useUpdateModelloDispositivo,
} from "@/api";
import { Controller, UseFormSetError, useForm } from "react-hook-form";
import { withOpeningReset } from "../../utils/withOpeningReset";
import { SmallLoading } from "../Loading";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import { addServerErrorsFactory } from "@/utils/addServerErrorsFactory";
import { AxiosError } from "axios";
import ModelloDispositivoMappaturaBitmaskDialog from "../ModelloDispositivoMappaturaBitmaskDialog";
import { useMemo, useState } from "react";
import ModelloDispositivoMappaturaEqualityDialog from "../ModelloDispositivoMappaturaEqualityDialog";
import {
  dispositivoTypes,
  getDispositivoTypeHelper,
} from "@/utils/dispositivoUtils";
import FileField from "../FileField";

const availableMaschere = Object.keys(MascheraDispositivo);

function ModelloDispositivoDialog({
  open = false,
  onClose,
  modello,
}: {
  open?: boolean;
  onClose?: () => void;
  modello?: ModelloDispositivoDto | null;
}) {
  const modelliDispositivo = useGetModelliDispositivo();

  const handleClose = () => {
    onClose?.();
  };

  const {
    register,
    control,
    handleSubmit,
    setError,
    formState: { errors, isValid },
    watch,
    setValue,
    resetField,
  } = useForm<ModelloDispositivoDto>({
    defaultValues: modello || {},
  });

  const { saveAsync, isLoading } = useMutator(modello, setError);

  const [mappaturaType, setMappaturaType] = useState<MappaturaAllarmiType>(
    modello?.mappaturaAllarmi?.type || MappaturaAllarmiType.Bitmask
  );

  const queryClient = useQueryClient();

  const onSubmit = async (data: ModelloDispositivoDto) => {
    try {
      await saveAsync(data);
      queryClient.invalidateQueries({
        queryKey: getGetModelliDispositivoQueryKey(),
      });
      // queryClient.invalidateQueries({ queryKey: getGetClientiQueryKey() });
      onClose?.();
    } catch (err) {}
  };

  const nomeModello = watch("modello");
  const alreadyExists = useMemo(() => {
    if (!nomeModello || !modelliDispositivo.data?.data) {
      return null;
    }

    return !!modelliDispositivo.data.data.find(
      (m) => m.modello.trim() === nomeModello.trim() && m.id !== modello?.id
    );
  }, [nomeModello, modelliDispositivo.data, modello]);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>
        {modello ? "Modifica modello" : "Creazione modello"}
      </DialogTitle>
      <DialogContent>
        <InputLabel htmlFor="user-modello">Modello</InputLabel>
        <TextField
          id="modello-nome"
          autoFocus
          {...register("modello", {
            required: "Campo obbligatorio",
            minLength: 1,
          })}
          error={!!errors.modello?.message}
          helperText={errors.modello?.message || null}
          fullWidth
        />
        {alreadyExists && (
          <FormHelperText error>
            Esiste già un modello dispositivo con questo nome
          </FormHelperText>
        )}

        <InputLabel htmlFor="dispositivo-type">Tipo</InputLabel>

        <Controller
          control={control}
          name="tipoDispositivo"
          rules={{ required: "Inserisci il tipo di dispositivo" }}
          render={({ field, fieldState: { error } }) => {
            return (
              <>
                <Select
                  {...field}
                  value={field.value ?? ""}
                  size="small"
                  sx={{ pt: 1 }}
                  id="dispositivo-type"
                >
                  {dispositivoTypes
                    .filter((t) => t !== DispositivoType.unknown)
                    .map((type) => {
                      return (
                        <MenuItem key={type} value={type}>
                          {getDispositivoTypeHelper(type)?.label}
                        </MenuItem>
                      );
                    })}
                </Select>
                {error && (
                  <FormHelperText error>{error?.message}</FormHelperText>
                )}
              </>
            );
          }}
        />

        <InputLabel htmlFor="user-maschera">Maschera registri</InputLabel>
        <Controller
          control={control}
          name="maschera"
          render={({ field, fieldState: { error } }) => {
            return (
              <>
                <Select
                  {...field}
                  value={field.value ?? ""}
                  size="small"
                  sx={{ pt: 1 }}
                  id="maschera"
                >
                  <MenuItem value="">
                    <em>Nessuna maschera registri</em>
                  </MenuItem>
                  {availableMaschere.map((maschera) => {
                    return (
                      <MenuItem key={maschera} value={maschera}>
                        {maschera}
                      </MenuItem>
                    );
                  })}
                </Select>
                {error && (
                  <FormHelperText error>{error?.message}</FormHelperText>
                )}
              </>
            );
          }}
        />

        <InputLabel htmlFor="user-image">Immagine</InputLabel>
        <Controller
          name="image"
          control={control}
          rules={{ required: "Seleziona un file" }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <>
              <FileField
                value={value}
                onChange={(f) => {
                  onChange(f);
                  if (f?.id) {
                    setValue("imageId", f?.id);
                  } else {
                    resetField("imageId");
                  }
                }}
              />
              {error && <FormHelperText error>{error?.message}</FormHelperText>}
            </>
          )}
        />

        <InputLabel>Tipo di mappatura degli errori</InputLabel>
        <Select
          value={mappaturaType}
          onChange={(e) => {
            setMappaturaType(e.target.value as MappaturaAllarmiType);
          }}
        >
          <MenuItem value={MappaturaAllarmiType.Bitmask}>Bitmask</MenuItem>
          <MenuItem value={MappaturaAllarmiType.Equality}>Equality</MenuItem>
        </Select>

        {mappaturaType === MappaturaAllarmiType.Bitmask && (
          <div>
            <InputLabel>Bitmask</InputLabel>
            <Controller
              name={"mappaturaAllarmi"}
              control={control}
              render={({ field }) => {
                return (
                  <ModelloDispositivoMappaturaBitmaskDialog
                    bitmask={field.value}
                    onChange={(event, bitmask) => {
                      field.onChange(bitmask);
                    }}
                  />
                );
              }}
            />
          </div>
        )}

        {mappaturaType === MappaturaAllarmiType.Equality && (
          <div>
            <InputLabel>Valori specifici</InputLabel>
            <Controller
              name={"mappaturaAllarmi"}
              control={control}
              render={({ field }) => {
                return (
                  <ModelloDispositivoMappaturaEqualityDialog
                    equality={field.value}
                    onChange={(event, bitmask) => {
                      field.onChange(bitmask);
                    }}
                  />
                );
              }}
            />
          </div>
        )}
      </DialogContent>
      <DialogActions>
        {isLoading && <SmallLoading sx={{ mr: 1 }} />}
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={!isValid || isLoading || alreadyExists !== false}
          onClick={handleSubmit(onSubmit)}
        >
          {modello ? "Salva modello " : "Crea modello"}
        </Button>
        <Button
          variant="contained"
          color="light"
          disabled={isLoading}
          onClick={handleClose}
        >
          Annulla
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default withOpeningReset(ModelloDispositivoDialog);

function useMutator(
  modello: ModelloDispositivoDto | null | undefined,
  setError: UseFormSetError<ModelloDispositivoDto>
): {
  isLoading: boolean;

  error: AxiosError<unknown, any> | null;
  saveAsync: (data: ModelloDispositivoDto) => Promise<any>;
} {
  const isNew = !modello?.id;

  const create = useCreateModelloDispositivo({
    mutation: {
      onSuccess: () => {
        toast.success("Modello creato!");
      },
      onError: addServerErrorsFactory(setError, () => {
        toast.error("Errore nella richiesta");
      }),
    },
  });

  const update = useUpdateModelloDispositivo({
    mutation: {
      onSuccess: () => {
        toast.success("Modello salvato!");
      },
      onError: addServerErrorsFactory(setError, () => {
        toast.error("Errore nella richiesta");
      }),
    },
  });

  const { isLoading, error } = isNew ? create : update;

  return {
    isLoading,
    error,
    saveAsync: async (data: ModelloDispositivoDto) => {
      return isNew
        ? create.mutateAsync({ data: data as ModelloDispositivoDto })
        : update.mutateAsync({
            id: modello.id!,
            data: data as ModelloDispositivoDto,
          });
    },
  };
}
